import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TpvOperatorRepository, OperatorListFilters } from '@app/domain/ports/tpv/tpv-operator.repository';
import {
  TpvOperatorResult,
  TpvOperatorListItem,
  TpvOperatorDetail,
  OperatorMerchants,
} from '@app/domain/models/tpv/tpv-operator.model';
import {
  CreateTpvOperatorRequest,
  UpdateTpvOperatorRequest,
  TpvOperatorResponseDto,
  TpvOperatorListResponseDto,
  TpvOperatorDetailResponseDto,
  OperatorMerchantsDto,
  OperatorMerchantsRequest,
} from '@app/data/dtos/tpv/tpv-operator.dto';
import {
  mapTpvOperatorResult,
  mapTpvOperatorListItem,
  mapTpvOperatorDetail,
  mapOperatorMerchants,
} from '@app/data/mappers/tpv/tpv-operator.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/**
 * Operadores TPV nativos (`/api/v1/tpv/companies/<id_company>/operators/`, micro TPV,
 * ManageTpvCompanyPermission). Auth vía interceptores estándar. Cubre alta (§4.A),
 * listado (§4.B) y la allow-list de comercios por operador (§4.C).
 *
 * `idCompany` acepta el PK ENTERO de la org (org_id_company, histórico) o el
 * UUID `id_company` — el back resuelve ambos; el UUID es la única vía para
 * empresas nuevas que aún no tienen org espejo.
 */
@Injectable()
export class TpvOperatorRemoteDataSource extends TpvOperatorRepository {
  private readonly http = inject(HttpClient);

  override createOperator(
    idCompany: number | string,
    request: CreateTpvOperatorRequest,
    idempotencyKey?: string,
  ): Observable<TpvOperatorResult> {
    // Idempotency-Key (mismo patrón que la provisión de comercios): dedup del
    // alta cuando la 1ª petición se cuelga pero el usuario sí se creó.
    const options = idempotencyKey
      ? { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) }
      : {};
    return this.http
      .post<TpvEnvelope<TpvOperatorResponseDto> | TpvOperatorResponseDto>(
        tpvUrl(`companies/${idCompany}/operators/`),
        request,
        options,
      )
      .pipe(map((resp) => mapTpvOperatorResult(unwrapTpv(resp))));
  }

  override listOperators(
    idCompany: number | string,
    filters: OperatorListFilters = {},
  ): Observable<TpvOperatorListItem[]> {
    // Filtros server-side (mismas keys del contrato §4.B). CSV para multi-valor.
    const params = toHttpParams({
      search: filters.search,
      role_name: filters.roleName,
      status: filters.status && filters.status.length ? filters.status.join(',') : undefined,
      company_wide: filters.companyWide ? true : undefined,
    });
    return this.http
      .get<TpvEnvelope<TpvOperatorListResponseDto> | TpvOperatorListResponseDto>(
        tpvUrl(`companies/${idCompany}/operators/`),
        { params },
      )
      .pipe(
        map((resp) => {
          const payload = unwrapTpv(resp) as TpvOperatorListResponseDto;
          return (payload?.operators ?? []).map(mapTpvOperatorListItem);
        }),
      );
  }

  override getOperator(
    idCompany: number | string,
    idUser: number | string,
  ): Observable<TpvOperatorDetail> {
    return this.http
      .get<TpvEnvelope<TpvOperatorDetailResponseDto> | TpvOperatorDetailResponseDto>(
        tpvUrl(`companies/${idCompany}/operators/${idUser}/`),
      )
      .pipe(
        map((resp) => {
          const payload = unwrapTpv(resp) as TpvOperatorDetailResponseDto;
          return mapTpvOperatorDetail(payload?.operator ?? {});
        }),
      );
  }

  override updateOperator(
    idCompany: number | string,
    idUser: number | string,
    request: UpdateTpvOperatorRequest,
  ): Observable<TpvOperatorDetail> {
    return this.http
      .patch<TpvEnvelope<TpvOperatorDetailResponseDto> | TpvOperatorDetailResponseDto>(
        tpvUrl(`companies/${idCompany}/operators/${idUser}/`),
        request,
      )
      .pipe(
        map((resp) => {
          const payload = unwrapTpv(resp) as TpvOperatorDetailResponseDto;
          return mapTpvOperatorDetail(payload?.operator ?? {});
        }),
      );
  }

  override deleteOperator(
    idCompany: number | string,
    idUser: number | string,
  ): Observable<void> {
    return this.http
      .delete<unknown>(tpvUrl(`companies/${idCompany}/operators/${idUser}/`))
      .pipe(map(() => void 0));
  }

  override getOperatorMerchants(
    idCompany: number | string,
    idUser: number | string,
  ): Observable<OperatorMerchants> {
    return this.http
      .get<TpvEnvelope<OperatorMerchantsDto> | OperatorMerchantsDto>(
        tpvUrl(`companies/${idCompany}/operators/${idUser}/merchants/`),
      )
      .pipe(map((resp) => mapOperatorMerchants(unwrapTpv(resp))));
  }

  override replaceOperatorMerchants(
    idCompany: number | string,
    idUser: number | string,
    idMerchants: string[],
  ): Observable<OperatorMerchants> {
    const body: OperatorMerchantsRequest = { id_merchants: idMerchants };
    return this.http
      .put<TpvEnvelope<OperatorMerchantsDto> | OperatorMerchantsDto>(
        tpvUrl(`companies/${idCompany}/operators/${idUser}/merchants/`),
        body,
      )
      .pipe(map((resp) => mapOperatorMerchants(unwrapTpv(resp))));
  }

  override addOperatorMerchants(
    idCompany: number | string,
    idUser: number | string,
    idMerchants: string[],
  ): Observable<OperatorMerchants> {
    const body: OperatorMerchantsRequest = { id_merchants: idMerchants };
    return this.http
      .post<TpvEnvelope<OperatorMerchantsDto> | OperatorMerchantsDto>(
        tpvUrl(`companies/${idCompany}/operators/${idUser}/merchants/`),
        body,
      )
      .pipe(map((resp) => mapOperatorMerchants(unwrapTpv(resp))));
  }

  override removeOperatorMerchant(
    idCompany: number | string,
    idUser: number | string,
    idMerchant: string,
  ): Observable<OperatorMerchants> {
    return this.http
      .delete<TpvEnvelope<OperatorMerchantsDto> | OperatorMerchantsDto>(
        tpvUrl(`companies/${idCompany}/operators/${idUser}/merchants/${idMerchant}/`),
      )
      .pipe(map((resp) => mapOperatorMerchants(unwrapTpv(resp))));
  }
}
